import { ArrowRight, BookOpen, Brain, Code, Users, Zap, Award, Star, Clock, CheckCircle, ArrowDown, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackCourseClick } from '../components/GoogleAnalytics';
import { usePromotional } from '../contexts/PromotionalContext';
import { buildUdemyCourseUrl } from '../utils/udemyLinks';
import { getAiCourseImage } from '../config/courseImages';
import '../styles/scrollAnimations.css';

const PathsPage = () => {
  const courses = [
    {
      id: 0,
      title: "ZERO Code AI Tests: Automate Software Tests in Plain English",
      subtitle: "No-Code AI Testing - NEW",
      description: "Automate software tests without writing a single line of code using AI. Learn to describe tests in plain English and let AI generate, execute, and maintain them for you — perfect for testers, BAs, and developers who want fast, reliable test automation.",
      longDescription: "This breakthrough course teaches you how to automate software testing using plain English instructions powered by AI. No programming experience required — describe your tests naturally and watch AI handle the rest.",
      level: "Beginner",
      duration: "10 hours",
      students: "1,000+",
      rating: 5.0,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-emerald-500 to-teal-500",
      borderColor: "border-emerald-200 hover:border-emerald-500/50",
      bgColor: "bg-emerald-50 hover:bg-emerald-100",
      skills: ["Zero Code Testing", "Plain English Tests", "AI Test Generation", "AI Test Execution", "Test Maintenance", "No-Code Automation"],
      udemy: "zero-code-ai-testing",
      whatYoullLearn: [
        "Write automated tests in plain English — no code required",
        "Use AI to generate and execute software tests",
        "Maintain and scale test suites with AI assistance",
        "Integrate no-code AI testing into your workflow"
      ]
    },
    {
      id: 1,
      title: "Using Gen AI & AI Agent in Software Automation Testing",
      subtitle: "Foundation Course - Start Here",
      description: "Start your AI testing journey with fundamentals of GenAI, prompt engineering, AI Agents, MCP server usage, making Selenium and Playwright test automation smarter with AI.",
      longDescription: "This foundational course introduces you to the world of Generative AI in software testing. You'll learn how to leverage AI for test case generation, automated test script creation, and intelligent bug detection.",
      level: "Beginner",
      duration: "8 hours",
      students: "8,000+",
      rating: 4.7,
      price: "Enroll on Udemy",
      icon: Brain,
      color: "from-blue-500 to-cyan-500",
      borderColor: "border-blue-200 hover:border-blue-500/50",
      bgColor: "bg-blue-50 hover:bg-blue-100",
      skills: ["GenAI Fundamentals", "Prompt Engineering", "AI Agents", "MCP Server", "Selenium AI", "Playwright AI"],
      udemy: "generative-ai-in-software-automation-testing",
      whatYoullLearn: [
        "GenAI fundamentals and core concepts",
        "Prompt engineering for testing scenarios",
        "AI-powered test case generation",
        "Integration with existing test frameworks"
      ]
    },
    {
      id: 2,
      title: "E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval",
      subtitle: "End-to-End AI Application Testing",
      description: "AI-powered applications are reshaping the software landscape — but how do you test them? Traditional QA methods fall short when your application thinks, reasons, and responds dynamically. Build a complete E2E testing strategy for ChatBots, AI Agents, RAG pipelines, and MCP Servers using DeepEval.",
      longDescription: "This comprehensive, hands-on course teaches you how to build a complete end-to-end testing strategy for modern AI systems — including ChatBots, AI Agents, Retrieval-Augmented Generation (RAG) pipelines, and MCP Servers — using DeepEval, the leading open-source LLM evaluation framework. Every concept is grounded in a real-world e-commerce AI chatbot application.",
      level: "Intermediate",
      duration: "14 hours",
      students: "500+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-teal-500 to-cyan-600",
      borderColor: "border-teal-200 hover:border-teal-500/50",
      bgColor: "bg-teal-50 hover:bg-teal-100",
      skills: ["DeepEval", "E2E AI Testing", "ChatBot Testing", "AI Agent Testing", "RAG Testing", "MCP Server Testing", "LLM Evaluation", "Real-World Projects"],
      udemy: "ai-testing-deepeval-chatbot-rag-agent-mcp",
      whatYoullLearn: [
        "Build a complete E2E testing strategy for AI-powered applications",
        "Test ChatBots, AI Agents, and RAG pipelines with DeepEval",
        "Evaluate MCP Servers using DeepEval's evaluation framework",
        "Apply testing to a real-world e-commerce AI chatbot application"
      ]
    },
    {
      id: 3,
      title: "AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS",
      subtitle: "Testing AI Applications",
      description: "Learn to test AI applications using specialized frameworks like DeepEval, RAGAs, HuggingFace Evaluate, Confident AI along with using Ollama with Local LLMs for comprehensive AI validation. Testing AI Agents, MCP Server, ChatBots, LLMs.",
      longDescription: "Dive deep into testing AI applications with industry-standard frameworks. Master DeepEval for LLM evaluation, RAGAs for retrieval systems, and Ollama for local AI testing.",
      level: "Beginner",
      duration: "12 hours",
      students: "1,500+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Code,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-200 hover:border-green-500/50",
      bgColor: "bg-green-50 hover:bg-green-100",
      skills: ["DeepEval Framework", "RAGAs Testing", "HuggingFace Evaluate", "Confident AI", "Ollama & Local LLMs", "AI Agents Testing", "MCP Server Testing", "ChatBot Testing"],
      udemy: "ai-testing-deepeval-ragas-ollama",
      whatYoullLearn: [
        "DeepEval framework mastery",
        "RAGAs for retrieval testing",
        "Ollama local AI testing",
        "LLM performance evaluation"
      ]
    },
    {
      id: 4,
      title: "Build Agentic AI & Multi-Agent System for Software Testing",
      subtitle: "Advanced Multi-Agent Systems",
      description: "Learn Building and Testing AI Agent, ChatBot, RAG with LangChain and LangSmith using Ollama and Local LLMs and RAGAs.",
      longDescription: "Master the most advanced AI testing concepts with multi-agent systems. Learn to orchestrate multiple AI agents for comprehensive testing workflows in enterprise environments.",
      level: "Intermediate",
      duration: "18 hours",
      students: "3,000+",
      rating: 4.8,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      borderColor: "border-orange-200 hover:border-orange-500/50",
      bgColor: "bg-orange-50 hover:bg-orange-100",
      skills: ["LangChain", "LangSmith", "MCP Server", "ChatBot", "AI Agents", "RAG"],
      udemy: "genai-multi-agent-software-testing",
      whatYoullLearn: [
        "Multi-agent architecture design",
        "Agent communication protocols",
        "Enterprise testing workflows",
        "Advanced automation strategies"
      ]
    },
    {
      id: 5,
      title: "Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents",
      subtitle: "Practical AI Security Testing - NEW",
      description: "Learn practical AI security testing with Promptfoo by attacking a real AI-powered e-commerce application. Test RAG apps, chatbots, and AI agents for prompt injection, jailbreaks, PII leakage, hallucination, excessive agency, policy violations, and knowledge-base poisoning.",
      longDescription: "Build repeatable AI security tests with Promptfoo using targets, providers, plugins, policies, strategies, graders, and assertions. Run single-turn and multi-turn attacks, analyze red-team reports, and strengthen LLM-powered applications against real-world OWASP risks.",
      level: "Beginner",
      duration: "8 hours",
      students: "47+",
      rating: 5.0,
      price: "Enroll on Udemy",
      icon: ShieldAlert,
      color: "from-red-500 to-rose-600",
      borderColor: "border-red-200 hover:border-red-500/50",
      bgColor: "bg-red-50 hover:bg-red-100",
      skills: ["Promptfoo", "AI Red Teaming", "OWASP for LLM Apps", "Jailbreak Testing", "Prompt Injection", "RAG Security", "PII Leakage", "Security Reports"],
      udemy: "ai-red-teaming-promptfoo-rag-chatbots-agents",
      whatYoullLearn: [
        "Configure Promptfoo targets, providers, plugins, policies, strategies, and assertions",
        "Run jailbreak, prompt-injection, PII-leakage, hallucination, and excessive-agency attacks",
        "Red team RAG applications for grounding, source attribution, and knowledge-base poisoning",
        "Analyze Promptfoo reports and build repeatable AI security regression tests"
      ]
    },
    {
      id: 6,
      title: "AI-Driven Test Automation: Playwright, Selenium, LLMs & More",
      subtitle: "Intelligent Self-Healing Test Frameworks",
      description: "Build intelligent, resilient test automation frameworks that leverage local LLMs to reduce maintenance, improve test stability, and accelerate bug detection with AI-powered self-healing locators for Selenium & Playwright.",
      longDescription: "Transform your test automation with AI-powered intelligence. Master building self-healing frameworks using local LLMs via Ollama, implementing intelligent locator strategies, and creating vision-based testing with zero flaky tests.",
      level: "Intermediate",
      duration: "15 hours",
      students: "82+",
      rating: 5.0,
      price: "Enroll on Udemy",
      icon: Code,
      color: "from-indigo-500 to-blue-500",
      borderColor: "border-indigo-200 hover:border-indigo-500/50",
      bgColor: "bg-indigo-50 hover:bg-indigo-100",
      skills: ["Self-Healing Locators", "AI-Powered Selenium", "AI-Powered Playwright", "Local LLMs with Ollama", "Prompt Engineering", "Visual Testing with Vision Models", "Locator Strategy", "Zero Flaky Tests"],
      udemy: "ai-test-automation-selenium-playwright-llms",
      whatYoullLearn: [
        "AI-powered self-healing locator implementation",
        "Local LLM integration with Ollama for testing",
        "Building intelligent Selenium & Playwright frameworks",
        "Vision model testing for visual validation"
      ]
    },
    {
      id: 7,
      title: "LangChain for Beginners : Building AI Agents, RAG & ChatBots",
      subtitle: "AI System Development",
      description: "Master building and testing AI agents, chatbots, and RAG systems using AutoGen, LangChain with Ollama and local LLMs. See how you can empower existing test automation workflow with Modern AI Agents and simplify automation in every aspect.",
      longDescription: "Learn to build sophisticated AI agents and chatbots using RAG (Retrieval Augmented Generation) and LangChain. Focus on enterprise-grade implementations and testing strategies.",
      level: "Intermediate",
      duration: "15 hours",
      students: "1,200+",
      rating: 4.8,
      price: "Enroll on Udemy",
      icon: Users,
      color: "from-purple-500 to-pink-500",
      borderColor: "border-purple-200 hover:border-purple-500/50",
      bgColor: "bg-purple-50 hover:bg-purple-100",
      skills: ["AI Agent Development", "AutoGen Framework", "LangChain Integration", "ChatBot Testing", "RAG Systems", "Ollama & Local LLMs", "Test Automation Enhancement", "Modern AI Workflows"],
      udemy: "build-ai-agent-chatbot-rag-langchain-local-llm",
      whatYoullLearn: [
        "AI agent architecture and development",
        "ChatBot creation and optimization",
        "RAG system implementation",
        "LangChain and local LLM integration"
      ]
    },
    {
      id: 8,
      title: "LLM Fine-Tuning for Beginners: HuggingFace & Unsloth",
      subtitle: "Advanced AI Specialization",
      description: "Understand Transformer models and learn how to Test, Evaluate, and Train them with HuggingFace NLP library.",
      longDescription: "Become an AI expert with HuggingFace ecosystem. Learn model fine-tuning, custom model development, and advanced AI implementations for specialized testing scenarios.",
      level: "Expert",
      duration: "20 hours",
      students: "1,200+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Award,
      color: "from-violet-500 to-purple-600",
      borderColor: "border-violet-200 hover:border-violet-500/50",
      bgColor: "bg-violet-50 hover:bg-violet-100",
      skills: ["HuggingFace", "NLP", "Model Fine-tuning", "Custom AI Model", "Agentic AI"],
      udemy: "ai-with-huggingface",
      whatYoullLearn: [
        "Advanced model fine-tuning techniques",
        "HuggingFace ecosystem mastery",
        "Custom AI model development",
        "Production AI model deployment"
      ]
    }
  ];

  // Scroll animation hooks for each course
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const courseRefs = courses.map(() => useScrollAnimation({ threshold: 0.2, delay: 200 }));
  
  // Get promotional context for coupon codes
  const { config, isActive } = usePromotional();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white overflow-hidden hero-scroll-fade ${heroVisible ? 'animate' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/20 to-emerald-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Your Path to <span className="text-green-100">AI QA Engineering</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-green-50 max-w-4xl mx-auto leading-relaxed mb-6 md:mb-8 px-2">
              Follow our structured learning path from GenAI fundamentals to advanced multi-agent systems. 
              Transform your career with comprehensive AI testing expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <div className="flex items-center gap-2 text-green-100 text-sm sm:text-base">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="font-semibold">370,000+ students already enrolled</span>
              </div>
              <div className="flex items-center gap-2 text-green-100 text-sm sm:text-base">
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>9 comprehensive courses</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Path Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Complete Learning <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Journey</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Progress through each course sequentially to build comprehensive AI testing expertise
            </p>
          </div>

          {/* Course Path */}
          <div className="relative">
            {/* Central Path Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-green-500 via-purple-500 via-orange-500 to-violet-500 opacity-30"></div>

            {courses.map((course, index) => {
              const Icon = course.icon;
              const isEven = index % 2 === 0;
              const { ref, isVisible } = courseRefs[index];
              
              return (
                <div key={course.id} className="relative mb-8 md:mb-16 last:mb-0">
                  {/* Course Card */}
                  <div 
                    ref={ref}
                    className={`section-reveal ${isVisible ? 'animate' : ''} ${isEven ? 'md:pr-8 md:ml-0 md:text-right' : 'md:pl-8 md:ml-auto md:text-left'} md:w-1/2 w-full`}
                  >
                    <div className={`group relative bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border-2 ${course.borderColor} transition-all duration-500 hover:shadow-2xl transform hover:scale-[1.02] cursor-pointer ${course.bgColor}`}>
                      
                      {/* Course Number Badge */}
                      <div className={`absolute ${isEven ? 'md:-right-4' : 'md:-left-4'} -top-3 md:-top-4 right-4 md:right-auto w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {index + 1}
                      </div>

                      {/* Course Header */}
                      <div className={`flex items-start gap-3 md:gap-6 mb-4 md:mb-6 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                        <div className="relative w-28 md:w-36 aspect-video rounded-lg md:rounded-xl overflow-hidden border border-white/80 shadow-lg flex-shrink-0 bg-gray-100">
                          <img
                            src={getAiCourseImage(course.udemy)}
                            alt={`${course.title} course banner`}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className={`absolute bottom-1.5 ${isEven ? 'md:left-1.5' : 'left-1.5'} w-7 h-7 md:w-8 md:h-8 rounded-lg bg-gradient-to-r ${course.color} flex items-center justify-center shadow-lg ring-1 ring-white/70`}>
                            <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" aria-hidden="true" />
                          </div>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                            <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                              course.level === 'Beginner' ? 'bg-green-100 text-green-700' :
                              course.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                              course.level === 'Advanced' ? 'bg-orange-100 text-orange-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {course.level}
                            </span>
                            <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" />
                              {course.duration}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1">
                              <Star className="w-3 h-3 md:w-4 md:h-4 text-yellow-400 fill-yellow-400 star-rating-glow" />
                              {course.rating} ({course.students} students)
                            </span>
                          </div>
                          
                          <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-1 md:mb-2 leading-tight">{course.title}</h3>
                          <p className="text-base md:text-lg font-semibold text-green-600 mb-2 md:mb-3">{course.subtitle}</p>
                        </div>
                      </div>

                      {/* Course Description */}
                      <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6 leading-relaxed">{course.description}</p>

                      {/* What You'll Learn */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">What You'll Learn:</h4>
                        <div className="space-y-2">
                          {course.whatYoullLearn.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex items-start gap-3">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Skills */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Key Skills:</h4>
                        <div className="flex flex-wrap gap-1 md:gap-2">
                          {course.skills.map((skill, skillIndex) => (
                            <span 
                              key={skillIndex}
                              className="px-2 md:px-3 py-1 bg-gray-100 border border-gray-200 rounded-md md:rounded-lg text-xs md:text-sm text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Action Button */}
                      <a
                        href={buildUdemyCourseUrl(course.udemy, config, isActive)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackCourseClick(course.title, buildUdemyCourseUrl(course.udemy, config, isActive))}
                        className={`inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r ${course.color} text-white font-semibold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group/btn text-sm md:text-base`}
                      >
                        <BookOpen className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                        {course.price}
                        <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>

                  {/* Path Connector - Hidden on mobile */}
                  {index < courses.length - 1 && (
                    <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -bottom-8 z-10">
                      <div className={`w-8 h-8 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center shadow-lg`}>
                        <ArrowDown className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Completion Section */}
          <div className="mt-12 md:mt-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl md:rounded-2xl p-6 md:p-8 text-white text-center">
            <div className="max-w-3xl mx-auto">
              <Award className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6" />
              <h3 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Congratulations! 🎉</h3>
              <p className="text-base md:text-xl text-green-100 mb-4 md:mb-6 leading-relaxed">
                Upon completing this learning path, you'll be a certified AI QA Engineering expert,
                ready to lead AI testing initiatives in any organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Link
                  to="/courses"
                  className="bg-white text-green-600 px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-green-50 transition-colors text-sm md:text-base"
                >
                  Browse All Courses
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-white text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors text-sm md:text-base"
                >
                  Get Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PathsPage;
